// Bulk alarm documentation (larmunderlag) helper for the SEC-218 bulk update.
//
// The bulk editor opens with every field empty; support fills in only the
// fields they want to change; on save only those fields are written to every
// selected device. This module turns the bulk form model into that sparse
// payload (and reports which fields changed, for the save-confirmation popup).

const PERSONAL_ALARM_KEYS = ["reporting", "risk_profile", "name", "home_address", "work_address", "transport", "registration_number", "model", "color"];
const EXTERNAL_CONTRACTOR_KEYS = ["name", "phone", "object_id"];
const CONTACT_PERSON_KEYS = ["name", "function", "phone", "weekday", "saturday", "sunday"];

// How many contact-person rows a larmunderlag holds. The create-device page and
// automatic device creation from an order both store three, so the bulk editor
// and the single-device editor show three as well.
export const CONTACT_PERSON_ROWS = 3;

export function emptyContactPerson() {
  return { name: "", function: "", phone: "", weekday: "", saturday: "", sunday: "" };
}

function isFilled(value) {
  return value !== null && value !== undefined && String(value).trim() !== "";
}

function anyFilled(obj, keys) {
  return !!obj && keys.some((k) => isFilled(obj[k]));
}

// A contact row is "filled" when support typed something into any of its fields.
export function isContactRowFilled(row) {
  return !!row && CONTACT_PERSON_KEYS.some((k) => isFilled(row[k]));
}

function pickContactKeys(row) {
  const picked = {};
  CONTACT_PERSON_KEYS.forEach((k) => {
    picked[k] = isFilled(row[k]) ? String(row[k]).trim() : "";
  });
  return picked;
}

// An empty bulk form: nothing is pre-filled from any device (SEC-218 v2).
export function emptyBulkForm() {
  return {
    alarm_central: "",
    seller: "", // added 2026-09-08: the seller can be set for many devices at once
    alarm_unit_name: "",
    from_address: { street: "", zip: "", city: "" },
    business_description: "",
    selected_alarm_sort: "",
    risk_profile: "",
    user: "",
    reference_number: "",
    alarm_password: "",
    personal_password: "",
    comment: "",
    man_down: false,
    direct_dispatch: "", // "" no change, "on" or "off" (SEC-373)
    agreement_number: "",
    device_status: "",
    // Fixed at CONTACT_PERSON_ROWS rows, and the rows are positional: row 1
    // edits contact person 1 on every device, row 2 contact person 2, and so
    // on. An empty row means "leave that contact person alone".
    contact_persons: Array.from({ length: CONTACT_PERSON_ROWS }, emptyContactPerson),
    personal_alarm: { reporting: "", risk_profile: "", name: "", home_address: "", work_address: "", transport: "", registration_number: "", model: "", color: "" },
    external_contractor: { name: "", phone: "", object_id: "" },
  };
}

/**
 * The positional contact-person patch for the bulk payload.
 *
 * The bulk rows line up with a device's contact persons by position, so an
 * untouched row must not wipe the contact person standing in that slot: a bulk
 * update that fills in only contact person 1 has to leave contact persons 2 and
 * 3 of every device exactly as they were. Untouched rows are therefore sent as
 * null ("no change for this slot") instead of being dropped from the array -
 * dropping them would shift the rows below up, so filling only row 3 would
 * overwrite contact person 1.
 *
 * Returns null when support filled in no contact row at all.
 */
// The address fields a bulk edit can change, in the same order as the server's
// copy in securecloud-server/utils/bulk-documentation.js - change both together.
export const FROM_ADDRESS_KEYS = ["street", "zip", "city"];

/**
 * Merge an address patch into a device's existing from_address. Only the keys
 * present on the patch are written, so a bulk edit of the city alone leaves the
 * street and postal code on the device untouched (SEC-364 C1).
 */
export function mergeFromAddress(existing, patch) {
  const current = existing && typeof existing === "object" ? existing : {};
  const changes = patch && typeof patch === "object" ? patch : {};

  const merged = { ...current };
  FROM_ADDRESS_KEYS.forEach((key) => {
    if (!Object.prototype.hasOwnProperty.call(changes, key)) return;
    merged[key] = changes[key] === null || changes[key] === undefined ? "" : String(changes[key]).trim();
  });
  return merged;
}

export function buildContactPersonPatch(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const patch = list.map((row) => (isContactRowFilled(row) ? pickContactKeys(row) : null));
  // trailing "no change" slots carry no information
  while (patch.length > 0 && patch[patch.length - 1] === null) patch.pop();
  return patch.length > 0 ? patch : null;
}

/**
 * Apply a positional contact patch to a device's existing contact persons.
 *
 * A filled slot replaces that contact person as a whole - a half-filled row is
 * the person support typed, so the previous person's function or call hours must
 * not survive underneath it. A null slot keeps the device's own row. The result
 * is always padded to CONTACT_PERSON_ROWS, so a device left with one contact
 * person still offers three editable rows afterwards.
 *
 * The server runs the same merge on save (utils/bulk-documentation.js); this
 * copy is what the highlighted PDF preview is built from.
 */
export function mergeContactPersons(existing, patch) {
  const current = Array.isArray(existing) ? existing : [];
  const slots = Array.isArray(patch) ? patch : [];
  const length = Math.max(current.length, slots.length, CONTACT_PERSON_ROWS);

  const merged = [];
  for (let i = 0; i < length; i++) {
    const replacement = slots[i];
    merged.push(pickContactKeys(replacement || current[i] || {}));
  }
  return merged;
}

/**
 * The device as it will look after the bulk save - what the highlighted PDF is
 * generated from. Only contact_persons needs more than a spread: it is a
 * positional patch, not a replacement (see buildContactPersonPatch).
 */
export function applyBulkDocumentation(alarm, documentation) {
  const applied = { ...alarm, ...documentation };
  if (documentation && Object.prototype.hasOwnProperty.call(documentation, "contact_persons")) {
    applied.contact_persons = mergeContactPersons(alarm && alarm.contact_persons, documentation.contact_persons);
  }
  if (documentation && Object.prototype.hasOwnProperty.call(documentation, "from_address")) {
    applied.from_address = mergeFromAddress(alarm && alarm.from_address, documentation.from_address);
  }
  return applied;
}

/**
 * Build the sparse bulk update payload from the bulk form.
 *
 * A scalar field is included only when it has a non-empty value (a field typed
 * then cleared is empty again -> excluded). On/off fields are included only
 * when turned on (off == default == "no change"). The replace-whole blocks
 * (personal alarm, external contractor) are included only when they contain any
 * data, and then replace that whole block on every device. Contact persons are
 * the exception: they are patched slot by slot, so filling one row leaves the
 * other contact persons of every device untouched.
 *
 * Returns { documentation, changedFields }:
 *   documentation - the object sent to PUT /alarms/bulk-documentation
 *   changedFields - keys that will be written (mapped to labels for the popup)
 */
export function buildSparseBulkPayload(bulkForm) {
  const documentation = {};
  const changedFields = [];

  const addScalar = (key, value) => {
    if (isFilled(value)) {
      documentation[key] = typeof value === "string" ? value.trim() : value;
      changedFields.push(key);
    }
  };

  addScalar("alarm_central", bulkForm.alarm_central);
  addScalar("seller", bulkForm.seller);
  addScalar("alarm_unit_name", bulkForm.alarm_unit_name);
  addScalar("business_description", bulkForm.business_description);
  addScalar("selected_alarm_sort", bulkForm.selected_alarm_sort);
  addScalar("risk_profile", bulkForm.risk_profile);
  addScalar("user", bulkForm.user);
  addScalar("reference_number", bulkForm.reference_number);
  addScalar("alarm_password", bulkForm.alarm_password);
  addScalar("personal_password", bulkForm.personal_password);
  addScalar("comment", bulkForm.comment);
  addScalar("agreement_number", bulkForm.agreement_number);
  addScalar("device_status", bulkForm.device_status);

  // from_address: the city for every alarm central, plus street and postal code
  // for Safe-4, which splits the address into three fields. Only the fields that
  // were filled in are sent, so the server leaves the rest of the device's
  // address alone (SEC-364 C1).
  const addressPatch = {};
  FROM_ADDRESS_KEYS.forEach((key) => {
    const value = bulkForm.from_address && bulkForm.from_address[key];
    if (isFilled(value)) addressPatch[key] = String(value).trim();
  });
  if (Object.keys(addressPatch).length > 0) {
    documentation.from_address = addressPatch;
    changedFields.push("from_address");
  }

  // on/off switches: written only when turned on
  if (bulkForm.man_down === true) {
    documentation.man_down = true;
    changedFields.push("man_down");
  }
  // guard dispatch can be switched off as well as on (SEC-373)
  if (bulkForm.direct_dispatch === "on" || bulkForm.direct_dispatch === "off") {
    documentation.direct_dispatch = bulkForm.direct_dispatch === "on";
    changedFields.push("direct_dispatch");
  }

  // positional patch: only the slots support filled in are written
  const contactPatch = buildContactPersonPatch(bulkForm.contact_persons);
  if (contactPatch) {
    documentation.contact_persons = contactPatch;
    changedFields.push("contact_persons");
  }

  // replace-whole blocks
  if (anyFilled(bulkForm.personal_alarm, PERSONAL_ALARM_KEYS)) {
    documentation.personal_alarm = { ...bulkForm.personal_alarm };
    changedFields.push("personal_alarm");
  }
  if (anyFilled(bulkForm.external_contractor, EXTERNAL_CONTRACTOR_KEYS)) {
    documentation.external_contractor = { ...bulkForm.external_contractor };
    changedFields.push("external_contractor");
  }

  return { documentation, changedFields };
}

// Which contact-person slots a patch writes, 1-based, for the confirmation popup.
export function patchedContactPositions(patch) {
  return (Array.isArray(patch) ? patch : []).reduce((positions, slot, i) => {
    if (slot) positions.push(i + 1);
    return positions;
  }, []);
}
